import {
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useState,
} from "react";
import {
  Breadcrumb,
  Breadcrumbs,
  type BreadcrumbOption,
} from "../breadcrumbs";
import { cn } from "../../utils/classes";
import { IdentityControl } from "./identity-control";
import { NavRail } from "./nav-rail";
import { SecondaryHeader } from "./secondary-header";
import { ThemeToggle } from "./theme-toggle";
import { TopHeader } from "./top-header";
import styles from "./app-shell.module.css";

export { AppLauncher } from "./app-launcher";
export type { AppLauncherItem, AppLauncherProps } from "./app-launcher";
export { NavRail, NavRailItem, NavRailSubItem } from "./nav-rail";
export type {
  NavRailItemProps,
  NavRailProps,
  NavRailSubItemProps,
} from "./nav-rail";
export { TopHeader } from "./top-header";
export type { TopHeaderProps } from "./top-header";
export { SecondaryHeader } from "./secondary-header";
export type { SecondaryHeaderProps } from "./secondary-header";
export { IdentityControl } from "./identity-control";
export type { IdentityControlProps } from "./identity-control";
export { RailAvatar } from "./identity-control/rail-avatar";
export { ThemeToggle } from "./theme-toggle";
export type { ThemeToggleProps } from "./theme-toggle";
export { VersionStamp } from "./version-stamp";
export type { VersionStampProps } from "./version-stamp";
export { SearchInput } from "./search-input";
export type { SearchInputProps } from "./search-input";
export { GlobalSearch } from "./global-search";
export type {
  GlobalSearchItem,
  GlobalSearchProps,
} from "./global-search";
export { SettingsMenu } from "./settings-menu";
export type {
  SettingsMenuItem,
  SettingsMenuProps,
} from "./settings-menu";
export {
  NotificationBell,
  timeAgo,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeed,
} from "./notification-bell";
export type { NotificationBellProps, Notification } from "./notification-bell";
export { StatusPill } from "./status-pill";
export type { StatusPillProps, StatusPillTone } from "./status-pill";

export type AppShellBreadcrumbItem = {
  title: string;
  uri?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  options?: BreadcrumbOption[];
};

export type AppShellProps = PropsWithChildren<{
  className?: string;

  // ---- Nav rail ----
  /** Logo/icon slot — stays pinned at the same position collapsed or expanded. */
  logo?: ReactNode;
  /** Wordmark text shown beside `logo` ONLY when the rail is expanded. */
  logoLabel?: ReactNode;
  /** App-icon nav items — wrap each in `<NavRailItem>`. */
  navItems?: ReactNode;
  /** Controlled expanded state for the rail. Persist this if you store it. */
  navExpanded?: boolean;
  defaultNavExpanded?: boolean;
  onNavExpandedChange?: (expanded: boolean) => void;
  navCollapsible?: boolean;

  /**
   * Nav items rendered in the footer, below the theme toggle and above the
   * identity control — for items that belong near account/chrome controls
   * (e.g. Settings) rather than in the primary scrollable `navItems` list.
   * Wrap each in `<NavRailItem>`, same as `navItems`.
   */
  footerNavItems?: ReactNode;

  /**
   * App-switcher control pinned to the very bottom of the rail. `<NavRail>`
   * renders this in its own section with a separator immediately above it.
   */
  appLauncher?: ReactNode;

  // ---- Identity control (bottom-left of the rail) ----
  /** Fully custom footer content; overrides the built-in `<IdentityControl>` below. */
  identity?: ReactNode;
  identityName?: string;
  identitySubtitle?: string;
  identityAvatarUrl?: string | null;
  onIdentityClick?: MouseEventHandler<HTMLButtonElement>;
  /** True when the current route IS wherever `onIdentityClick` navigates to
   * (e.g. already on `/profile`) — see `<IdentityControl>`'s own `active`
   * prop. */
  identityActive?: boolean;

  // ---- Theme toggle ----
  showThemeToggle?: boolean;

  /**
   * Bottom-of-rail version/changelog control, rendered directly below the
   * app launcher — pass a `<VersionStamp>` element. The caller builds it
   * (rather than this component taking a bare `version` string prop) since
   * it needs an `onClick` wired to whatever routing mechanism the host uses.
   */
  versionStamp?: ReactNode;

  // ---- Top header ----
  /** Canonical top-header breadcrumb data, rendered by AppShell itself. */
  breadcrumbItems?: AppShellBreadcrumbItem[];
  search?: ReactNode;
  notifications?: ReactNode;

  // ---- Secondary, page-level header ----
  secondaryHeader?: ReactNode;
}>;

/**
 * The Nazare app shell (`design.md` "app-shell"): a 64px collapsible nav
 * rail on the left, a 48px top header, an optional page-level secondary
 * header, and the page content. Composes `<NavRail>`, `<TopHeader>`,
 * `<SecondaryHeader>`, `<IdentityControl>`, and `<ThemeToggle>` — reach for
 * those directly if this composition doesn't fit.
 */
export function AppShell({
  className,
  logo,
  logoLabel,
  navItems,
  navExpanded,
  defaultNavExpanded = false,
  onNavExpandedChange,
  navCollapsible = true,
  footerNavItems,
  appLauncher,
  identity,
  identityName,
  identitySubtitle,
  identityAvatarUrl,
  onIdentityClick,
  identityActive = false,
  showThemeToggle = true,
  versionStamp,
  breadcrumbItems,
  search,
  notifications,
  secondaryHeader,
  children,
}: AppShellProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultNavExpanded);
  const expanded = navExpanded ?? internalExpanded;

  const handleExpandedChange = useCallback(
    (next: boolean) => {
      if (navExpanded === undefined) {
        setInternalExpanded(next);
      }
      onNavExpandedChange?.(next);
    },
    [navExpanded, onNavExpandedChange],
  );

  const identityFooter =
    identity ??
    (identityName ? (
      <IdentityControl
        name={identityName}
        subtitle={identitySubtitle}
        avatarUrl={identityAvatarUrl}
        expanded={expanded}
        active={identityActive}
        onClick={onIdentityClick}
      />
    ) : null);

  return (
    <div className={cn(styles.shell, className)}>
      <NavRail
        logo={logo}
        logoLabel={logoLabel}
        expanded={expanded}
        onExpandedChange={handleExpandedChange}
        collapsible={navCollapsible}
        bottom={
          (appLauncher || versionStamp) && (
            <>
              {appLauncher}
              {versionStamp}
            </>
          )
        }
        footer={
          (footerNavItems || identityFooter || showThemeToggle) && (
            <>
              {showThemeToggle && <ThemeToggle expanded={expanded} />}
              {footerNavItems}
              {identityFooter}
            </>
          )
        }
      >
        {navItems}
      </NavRail>
      <div className={styles.main}>
        <TopHeader
          breadcrumbs={
            breadcrumbItems && breadcrumbItems.length > 0 ? (
              <Breadcrumbs>
                {breadcrumbItems.map((item, index) => (
                  <Breadcrumb
                    key={`${item.title}:${index}`}
                    title={item.title}
                    uri={item.uri}
                    onClick={item.onClick}
                    options={item.options}
                    showChevron={index > 0}
                  />
                ))}
              </Breadcrumbs>
            ) : undefined
          }
          search={search}
          notifications={notifications}
        />
        {secondaryHeader && (
          <SecondaryHeader>{secondaryHeader}</SecondaryHeader>
        )}
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  );
}
